import { useEffect, useRef, useState } from 'preact/hooks';
import { base64ToBlob, segment, segmentWalls } from '../lib/api';
import { ImageInputError, loadPhoto, loadPhotoUrl } from '../lib/image';
import { coverage, loadMaskImage, scaleMask } from '../lib/mask';
import { cleanMask, heuristicConfidence, LOW_CONFIDENCE } from '../lib/maskPost';
import { preloadTurnstile } from '../lib/turnstile';
import { applyTheme, loadTenant, parseRoute, type Tenant, type TenantResult } from '../lib/tenant';
import { autoQuad, DEFAULT_QUAD } from '../render/autoQuad';
import type { Sample } from '../shared/schemas';
import { Editor } from './screens/Editor';
import { NotFound } from './screens/NotFound';
import { Start } from './screens/Start';
import { startScale, type Session } from './session';

export function App() {
  const [route] = useState(() => parseRoute(location));
  const [result, setResult] = useState<TenantResult | null>(null);

  useEffect(() => {
    loadTenant(route.slug).then((r) => {
      if (r.ok) applyTheme(r.tenant.config);
      setResult(r);
    });
  }, [route.slug]);

  if (!result) return <div class="min-h-(--fv-screen)" aria-busy="true" />;
  if (!result.ok) return <NotFound />;
  return <Visualizer tenant={result.tenant} />;
}

function Visualizer({ tenant }: { tenant: Tenant }) {
  const cfg = tenant.config;
  const [spaceTypeId, setSpaceTypeId] = useState(cfg.spaceTypes[0].id);
  /** Stem wall wanted (tenants with stemWall): detect walls with the floor. Off = floor only, faster. */
  const [wantStem, setWantStem] = useState(cfg.stemWall?.defaultOn ?? false);
  const [session, setSession] = useState<Session | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Increments per photo so a late detection result never lands on a newer photo. */
  const runRef = useRef(0);

  useEffect(preloadTurnstile, []);

  const update = (patch: Partial<Session>) => setSession((s) => (s ? { ...s, ...patch } : s));

  const baseSession = (photo: HTMLCanvasElement, spaceType: string): Session => ({
    photo,
    upload: null,
    uploadId: null,
    spaceTypeId: spaceType,
    mask: null,
    detectedMask: null,
    wallMask: null,
    stemWall: wantStem,
    wallStatus: 'idle',
    quad: DEFAULT_QUAD,
    quadManual: false,
    patternScale: startScale(cfg, session?.systemId ?? cfg.systems[0].id),
    systemId: session?.systemId ?? cfg.systems[0].id,
    colorId: session?.colorId ?? cfg.systems[0].colors[0].id,
    status: 'detecting',
    lowConfidence: false,
  });

  const onFile = async (file: File) => {
    setBusy(true);
    setError(null);
    let photo;
    try {
      photo = await loadPhoto(file);
    } catch (err) {
      console.error(err);
      setError(err instanceof ImageInputError ? err.message : 'Something went wrong opening that photo. Please try another one.');
      return;
    } finally {
      setBusy(false);
    }

    // Show the photo right away; colors can be browsed while the floor is detected.
    const { working, upload } = photo;
    const run = ++runRef.current;
    const walls = !!cfg.stemWall && wantStem;
    setSession({ ...baseSession(working, spaceTypeId), upload, wallStatus: walls ? 'loading' : 'idle' });

    const res = await segment(upload, cfg.slug, spaceTypeId, walls);
    if (run !== runRef.current) return;
    if (!res.ok) {
      console.warn('floor detection failed:', res.error);
      update({ uploadId: res.uploadId, status: 'failed', wallStatus: walls ? 'failed' : 'idle' });
      return;
    }
    try {
      const { uploadId, mask: png, wallMask: wallPng, width, height, confidence } = res.data;
      const raw = await loadMaskImage(base64ToBlob(png, 'image/png'), width, height);
      const clean = cleanMask(raw, width, height);
      const score = confidence >= 0 && confidence <= 1 ? confidence : heuristicConfidence(clean, width, height);
      const mask = scaleMask(clean, width, height, working.width, working.height);
      // Wall detection is optional: without it there is simply no stem wall.
      const wallMask = wallPng
        ? await loadMaskImage(base64ToBlob(wallPng, 'image/png'), working.width, working.height).catch(() => null)
        : null;
      if (run !== runRef.current) return;
      update({
        uploadId,
        mask,
        detectedMask: mask,
        wallMask,
        wallStatus: walls ? (wallMask ? 'ready' : 'failed') : 'idle',
        quad: autoQuad(mask, working.width, working.height),
        status: 'ready',
        lowConfidence: score < LOW_CONFIDENCE || coverage(mask) === 0,
      });
    } catch (err) {
      console.error(err);
      update({ uploadId: res.data.uploadId, status: 'failed' });
    }
  };

  /** Stem wall switched on after a floor-only upload: detect the walls now. */
  const loadWalls = async () => {
    const s = session;
    if (!s?.upload) return;
    const run = runRef.current;
    update({ wallStatus: 'loading' });
    const res = await segmentWalls(s.upload, cfg.slug, s.spaceTypeId);
    if (run !== runRef.current) return;
    if (!res.ok) {
      console.warn('wall detection failed:', res.error);
      update({ wallStatus: 'failed' });
      return;
    }
    const wallMask = await loadMaskImage(base64ToBlob(res.data.wallMask, 'image/png'), s.photo.width, s.photo.height).catch(() => null);
    if (run !== runRef.current) return;
    update({ wallMask, wallStatus: wallMask ? 'ready' : 'failed' });
  };

  const onStemWall = (on: boolean) => {
    setWantStem(on);
    update({ stemWall: on });
    if (on && session && (session.wallStatus === 'idle' || session.wallStatus === 'failed')) loadWalls();
  };

  const onSample = async (sample: Sample) => {
    runRef.current++;
    setBusy(true);
    setError(null);
    try {
      const photo = await loadPhotoUrl(tenant.asset(sample.image));
      const mask = await loadMaskImage(tenant.asset(sample.mask), photo.width, photo.height);
      setSpaceTypeId(sample.spaceType);
      setSession({
        ...baseSession(photo, sample.spaceType),
        uploadId: `sample:${sample.id}`,
        mask,
        detectedMask: mask,
        quad: sample.quad,
        status: 'ready',
      });
    } catch (err) {
      console.error(err);
      setError('That sample could not be loaded. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!session) {
    return (
      <Start
        tenant={tenant}
        spaceTypeId={spaceTypeId}
        onSpaceType={setSpaceTypeId}
        stemWall={wantStem}
        onStemWall={setWantStem}
        onFile={onFile}
        onSample={onSample}
        busy={busy}
        error={error}
      />
    );
  }
  const startOver = () => {
    runRef.current++;
    setSession(null);
  };
  return <Editor tenant={tenant} session={session} update={update} onStemWall={onStemWall} onStartOver={startOver} />;
}
